import React, { useEffect } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import Callback from '../pages/auth/Callback';
import SomethingWentWrong from '../common/SomethingWentWrong';
import ProtectedLayout from './ProtectedLayout';
import SidebarLayout from './SidebarLayout';
import OnboardingLayout from './OnboardingLayout';
import HomeLayout from './HomeLayout';
import AuthLayout from './AuthLayout';
import { getDefaultRoute, getProtectedOnboardingRoutes, getProtectedSidebarRoutes, trackSupportTicket } from './roleWisePageRoute';
import NotFound from '../pages/not-found';
import QuickTicket from '../pages/QuickTicket';
import SupportTrackLayout from './SupportTrackLayout';
import Videomeet from '../pages/videomeet/Videomeet';
import config from '../config';
import { GetCookieEnvName } from '../utils/common';
import Cookies from 'universal-cookie';
import PavPublicVerification from '../pages/PavPublicVerification';

const cookies = new Cookies();

export const redirectUrlAuth = 'redirectUrlAuth';

// Browser-compatible random string generator
function generateRandomString(length = 32) {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Browser-compatible state generator
function generateState(length = 32) {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

// Browser-compatible PKCE pair generator using Web Crypto API
async function generatePKCEPair() {
    const NUM_OF_BYTES = 32;
    const randomVerifier = generateRandomString(NUM_OF_BYTES);

    // Hash the verifier using SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(randomVerifier);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray));
    const challenge = hashBase64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    return { code_verifier: randomVerifier, code_challenge: challenge };
}


export const getAuthRoute = async ({ support_view = false, ticket_id } = {}) => {
    let url = config.authDomain;
    let redirectUri = `${window.location.origin}/auth/callback`;
    const state = generateState();
    const { code_challenge, code_verifier } = await generatePKCEPair();

    if (support_view) {
        url = `${url}/oauth/v2/authorize?client_id=${config.authClient}&response_type=code&ticket_id=${ticket_id}&code_challenge_method=S256&screen_hint=signup2&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&code_challenge=${code_challenge}`;
    } else {
        url = `${url}/oauth/v2/authorize?client_id=${config.authClient}&response_type=code&code_challenge_method=S256&screen_hint=login&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&code_challenge=${code_challenge}`;
    }
    localStorage.setItem(redirectUrlAuth, window.location.href);
    const name = GetCookieEnvName()
    cookies.set(`${name}:code_verifier`, code_verifier, { path: '/' });
    cookies.set(`${name}:state`, state, { path: '/' });

    return url;
}

export const getRoutes = ({ newLoggedUserData, isLoggedIn, sidebarList }) => {
    let protectedSidebarRoutes, protectedOnboardingRoute, defaultRoute;
    if (newLoggedUserData) {
        protectedSidebarRoutes = getProtectedSidebarRoutes({ is_onboarded: newLoggedUserData.user.is_onboarded, sidebarList, actions: newLoggedUserData.actions });
        protectedOnboardingRoute = getProtectedOnboardingRoutes({ is_onboarded: newLoggedUserData.user.is_onboarded });
        defaultRoute = getDefaultRoute({ is_onboarded: newLoggedUserData.user.is_onboarded, sidebarList, actions: newLoggedUserData.actions });
    }

    return createBrowserRouter(
        createRoutesFromElements(
            <>
                <Route path='/pav/:asset_id/:milestone_id' element={<PavPublicVerification />} errorElement={<SomethingWentWrong />} />
                <Route element={<AuthLayout />} errorElement={<SomethingWentWrong />}>
                    {/* <Route path='*' element={<UnderMaintainance />} /> */}
                    {isLoggedIn ? (
                        <Route element={<ProtectedLayout />}>
                            <Route element={<SidebarLayout />}>
                                {protectedSidebarRoutes?.map((route) => (
                                    <Route key={route.path} path={route.path} element={route.component} />
                                ))}
                            </Route>
                            {protectedOnboardingRoute && <Route path='/onboarding' element={<OnboardingLayout />}>
                                <Route key={protectedOnboardingRoute.path} path={protectedOnboardingRoute.path} element={protectedOnboardingRoute.component} />
                            </Route>}
                            <Route element={<SupportTrackLayout />}>
                                <Route path='/room/:room_id' element={<Videomeet />} />
                            </Route>
                            <Route path='/not-found' element={<NotFound />} />
                            <Route path='*' element={<Navigate to={defaultRoute || '/not-found'} />} />
                        </Route>
                    ) : (
                        <Route path='*' element={<AuthRedirect />} />
                    )}
                    <Route element={<SupportTrackLayout />}>
                        <Route path={trackSupportTicket.path} element={<QuickTicket />} />
                        {/* <Route path={detailsPath.path} element={<Details />} /> */}
                    </Route>
                    <Route element={<HomeLayout />}>
                        <Route path='/auth/callback' element={<Callback />} />
                        <Route path='*' element={<AuthRedirect />} />
                    </Route>
                </Route>
            </>
        )
    );
};


export const AuthRedirect = () => {
    useEffect(() => {
        const redirectToAuth = async () => {
            const url = await getAuthRoute();
            window.open(url, '_self');
        };
        redirectToAuth();
    }, []);

    return null;
};
